import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { combine, createEffect, createEvent, createStore, merge, sample } from "effector";
import { IOnboardingContract, setOnboardingDataById, clearOnboardingSession } from "../model";
import { IAnswersData, IScreenData, IOption, ScreenType } from "@features/shared/entities/onboarding";
import {
    onboardingCompleted,
    onboardingScreenCompleted,
    onboardingScreenPresented,
    productScreenPresented,
    registrationScreenPresented,
    secondScreenPresented
} from "@features/shared/events";
import { IPaymentContract } from "@features/payment/ui";
import { ISubscriptionContract } from "@features/subscription/model";

import { reset, delay } from "patronum";
import { IUserAuthContract } from "@features/student/auth/model";
import { IFlagsmithContract } from "@features/flagsmith/model";
import { CHALLENGE_100K_PAYWALL_STEP, CHALLENGE_100K_DISCOUNT_PAYWALL_STEP } from "@data/paywall/paywall100kChallenge";
import {
    CHALLENGE_100K_V3_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_100K_V3_PAYWALL_STEP,
    challenge100kV3Ids,
    challenge100kV3WithDiscountIds
} from "@data/paywall/paywall100kV3";
import {
    CHALLENGE_100K_V4_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_100K_V4_PAYWALL_STEP,
    challenge100kV4Ids,
    challenge100kV4WithDiscountIds
} from "@data/paywall/paywall100kV4";
import {
    CHALLENGE_100K_V5_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_100K_V5_PAYWALL_STEP,
    challenge100kV5Ids,
    challenge100kV5WithDiscountIds
} from "@data/paywall/paywall100kV5";
import {
    CHALLENGE_100K_V6_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_100K_V6_PAYWALL_STEP,
    challenge100kV6Ids,
    challenge100kV6WithDiscountIds
} from "@data/paywall/paywall100kV6";
import { PaymentApi } from "@features/shared/api/course-data.api";
import { contentLocaleChanged } from "@features/shared/locale";
import { IPaymentPlan, ISolidgatePlan, ITruegatePlan, SolidgatePlan } from "@features/shared/entities/payment";
import {
    challenge100kV7Ids,
    challenge100kV7WithDiscountIds,
    CHALLENGE_100K_V7_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_100K_V7_PAYWALL_STEP
} from "@data/paywall/paywall100kV7";
import {
    challenge100kV8Ids,
    challenge100kV8WithDiscountIds,
    CHALLENGE_100K_V8_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_100K_V8_PAYWALL_STEP
} from "@data/paywall/paywall100kV8";
import {
    challenge100kV8_3Ids,
    challenge100kV8_3WithDiscountIds,
    CHALLENGE_100K_V8_3_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_100K_V8_3_PAYWALL_STEP
} from "@data/paywall/paywall100kV8_3";
import {
    challenge100kV8_4Ids,
    challenge100kV8_4WithDiscountIds,
    CHALLENGE_100K_V8_4_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_100K_V8_4_PAYWALL_STEP
} from "@data/paywall/paywall100kV8_4";
import {
    challenge100kV9Ids,
    challenge100kV9WithDiscountIds,
    CHALLENGE_100K_V9_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_100K_V9_PAYWALL_STEP
} from "@data/paywall/paywall100kV9";
import {
    challengeAppleV2Ids,
    challengeAppleV2WithDiscountIds,
    CHALLENGE_APPLE_V2_DISCOUNT_PAYWALL_STEP,
    CHALLENGE_APPLE_V2_PAYWALL_STEP
} from "@data/paywall/paywallAppleV2";
import { ISubscription } from "@features/shared/entities/subscription";
import {
    buildNavigationPayload,
    parseRouteContext,
    RouteContext,
    getSessionIdFromSearch,
    buildUrlWithSessionId,
    shouldResetSession,
    findAnswerValueById
} from "./helpers";
import { OnboardingSessionResponse, OnboardingSessionState } from "@features/shared/entities/session";
import { IRouterContract } from "@features/shared/router";
import { OnboardingSessionsApiClient } from "@features/shared/api/onboardingSessions.api";
import { IAppContract } from "@features/app/model";
import { StudentApi } from "@features/shared/api/course-data.api";
import { AnalyticsService } from "@features/shared/analytics";

const getSubscriptionCurrency = (subscriptions: ISubscription[]) => {
    if (!subscriptions || subscriptions.length === 0) return "";
    const [subscription] = subscriptions;
    return subscription?.currency || subscription?.plan?.currency || "";
};

const CHALLENGE_100K_V7_ECOMMPAY_TAG = "challenge_100k_v7_ecommpay";
const CHALLENGE_100K_V7_ECOMMPAY_DISCOUNT_TAG = "challenge_100k_v7_ecommpay_discount";
const CHALLENGE_100K_V8_E_TAG = "challenge_100k_v8_e";
const CHALLENGE_100K_V8_E_DISCOUNT_TAG = "challenge_100k_v8_e_discount";

const savedAnswers = typeof window !== "undefined" ? sessionStorage.getItem("onboarding_answers") : null;
const initialAnswers: IAnswersData[] = savedAnswers ? JSON.parse(savedAnswers) : [];
const STEP_NAVIGATION_DELAY = 350;
const SESSION_SAVE_DEBOUNCE = 800;

export const nextStep = createEvent();
export const prevStep = createEvent();
export const requestNextStep = createEvent();
export const requestPrevStep = createEvent();
export const emailOnboardingStarted = createEvent();
const setStep = createEvent<number>();
const syncStep = createEvent<number>();
const setHydratedStep = createEvent<number>();
const setStepStream = merge([setStep, setHydratedStep]);
const stepPersistCommitted = createEvent<number>();
const saveAnswer = createEvent<IOption | IOption[]>();
const getAnswer = createEvent<number>();
const toggleCheckboxId = createEvent<string>();
const initOption = createEvent();
const initCheckboxes = createEvent();
const initInputValue = createEvent();
const initEmailInputValue = createEvent();
export const inputValueChanged = createEvent<string>();
export const emailInputValueChanged = createEvent<string>();
const stepPersistRequested = createEvent<number>();
const sessionInvalidated = createEvent();
const sessionHydrateRequested = createEvent<{ answers: IAnswersData[]; step: number }>();
const sessionUrlUpdateRequested = createEvent<string | null>();
const sessionEnsureRequested = createEvent<{ onboardingId: string; search: string }>();
const sessionSaveRequested = createEvent<{ state?: OnboardingSessionState }>();
const sessionPassiveSaveRequested = createEvent<void>();
const sessionStateUpdateRequested = createEvent<OnboardingSessionState>();
const sessionSaveDebounced = delay({
    source: sessionPassiveSaveRequested,
    timeout: SESSION_SAVE_DEBOUNCE
});
const onboardingContextReset = createEvent<string>();

const $onboardingSlugTracker = createStore<string | null>(null)
    .on(onboardingContextReset, (_, slug) => slug)
    .reset(clearOnboardingSession);

sample({
    clock: setOnboardingDataById,
    source: $onboardingSlugTracker,
    filter: (prev, slug) => prev !== slug,
    fn: (_, slug) => slug,
    target: onboardingContextReset
});
const ensureSessionFx = createEffect(async ({ onboardingId, search }: { onboardingId: string; search: string }) => {
    await AnalyticsService.deviceIdReady;
    const deviceId = localStorage.getItem("user_uuid")!;
    const routeSessionId = getSessionIdFromSearch(search);
    let session: OnboardingSessionResponse | null = null;

    if (routeSessionId) {
        try {
            session = await OnboardingSessionsApiClient.get(routeSessionId);
        } catch (error: any) {
            if (!shouldResetSession(error)) {
                throw error;
            }
        }
    }

    if (!session) {
        session = await OnboardingSessionsApiClient.create({
            onboardingId,
            deviceId,
            queryParams: search || undefined
        });
    }

    return { session, onboardingId };
});

const updateSessionFx = createEffect(
    async ({
        sessionId,
        answers,
        lastScreenIdx,
        state
    }: {
        sessionId: string;
        answers: IAnswersData[];
        lastScreenIdx: number;
        state?: OnboardingSessionState;
    }) => {
        if (state === "done") {
            return await OnboardingSessionsApiClient.update(sessionId, {
                state
            });
        }

        return await OnboardingSessionsApiClient.update(sessionId, {
            answers,
            lastScreenIdx,
            state
        });
    }
);

const persistAnswersFx = createEffect((answers: IAnswersData[]) => {
    if (typeof window === "undefined") return;
    sessionStorage.setItem("onboarding_answers", JSON.stringify(answers));
    const email = findAnswerValueById(answers, "email");
    if (email) {
        sessionStorage.setItem("email", email);
    } else {
        sessionStorage.removeItem("email");
    }
});

sample({
    clock: sessionEnsureRequested,
    target: ensureSessionFx
});

const $onboardingId = createStore("");
const $emailFromUrl = createStore<string | null>(null);
const $step = createStore(0);
const $totalSteps = createStore(0);
const $totalStepsWithoutPaywall = createStore(0);
const $data = createStore<IScreenData | null>(null);
const $selectedCheckboxes = createStore<string[]>([]).reset(
    nextStep,
    prevStep,
    sessionInvalidated,
    onboardingContextReset
);
const $selectedOption = createStore<IOption | null>(null).reset(sessionInvalidated, onboardingContextReset);
const $answers = createStore<IAnswersData[]>(initialAnswers)
    .on(sessionHydrateRequested, (_, { answers }) => answers)
    .reset([sessionInvalidated, onboardingContextReset]);
const $dependentAnswer = createStore<IOption | null>(null).reset(
    nextStep,
    prevStep,
    sessionInvalidated,
    onboardingContextReset
);
const $inputValue = createStore<string>("")
    .on(inputValueChanged, (s, p) => p)
    .reset(nextStep, prevStep, sessionInvalidated, onboardingContextReset);

const $emailInputValue = createStore<string>("")
    .on(emailInputValueChanged, (s, p) => p)
    .reset([sessionInvalidated, onboardingContextReset]);

const $pendingNav = createStore<"none" | "next" | "prev">("none")
    .on(requestNextStep, () => "next")
    .on(requestPrevStep, () => "prev");

// Flag to protect against synchronization during user navigation
// Resets after URL has actually been updated
const $userNavProtection = createStore(false).on([requestNextStep, requestPrevStep], () => true);
const $sessionId = createStore<string | null>(null)
    .on(ensureSessionFx.doneData, (_, { session }) => session.sessionId)
    .reset(sessionInvalidated, onboardingContextReset);
const $sessionReady = createStore(false)
    .on(ensureSessionFx.doneData, () => true)
    .on(sessionInvalidated, () => false)
    .reset(onboardingContextReset);
const $sessionState = createStore<OnboardingSessionState>("active")
    .on(ensureSessionFx.doneData, (_, { session }) => session.state)
    .on(updateSessionFx.doneData, (_, session) => session.state)
    .reset(sessionInvalidated, onboardingContextReset);
const $lastPersistedStep = createStore(-1)
    .on(sessionHydrateRequested, (_, { step }) => (step >= 0 ? step : -1))
    .on(stepPersistCommitted, (_, step) => (step >= 0 ? step : -1))
    .reset(sessionInvalidated, onboardingContextReset);

const trackScreenPresentedFx = createEffect<{ onboarding: string; screenCid: string; screenIndex: number }, void>(
    ({ onboarding, screenCid, screenIndex }) => {
        onboardingScreenPresented({ onboarding, screenCid, screenIndex });
    }
);

const trackSecondScreenPresentedFx = createEffect<{ onboarding: string; screenCid: string; screenIndex: number }, void>(
    ({ onboarding, screenCid, screenIndex }) => {
        secondScreenPresented({ onboarding, screenCid, screenIndex });
    }
);

const trackScreenCompletedFx = createEffect<
    {
        onboarding: string;
        screenCid: string;
        screenIndex: number;
        reply?: string | string[];
    },
    void
>(({ onboarding, screenCid, screenIndex, reply }) => {
    onboardingScreenCompleted({ onboarding, screenCid, screenIndex, reply });
});

const trackRegistrationScreenPresentedFx = createEffect<
    { onboarding: string; screenCid: string; screenIndex: number },
    void
>(({ onboarding, screenCid, screenIndex }) => {
    registrationScreenPresented({ onboarding, screenCid, screenIndex });
});

const trackOnboardingCompletedFx = createEffect<{ onboarding: string; screenCid: string; screenIndex: number }, void>(
    ({ onboarding, screenCid, screenIndex }) => {
        onboardingCompleted({ onboarding, screenCid, screenIndex });
    }
);

const trackProductScreenPresentedFx = createEffect<
    { onboarding: string; screenCid: string; screenIndex: number },
    void
>(({ onboarding, screenCid, screenIndex }) => {
    productScreenPresented({ onboarding, screenCid, screenIndex });
});

const routeContextUpdated = createEvent<RouteContext | null>();
const routeNavigationRequested = createEvent<{ ctx: RouteContext; step: number }>();
const routeNavigationPlanned = createEvent<{ url: string; replace: boolean }>();
const routeSyncStarted = createEvent();
const routeSyncFinished = createEvent();

const $routeContext = createStore<RouteContext | null>(null).on(routeContextUpdated, (_, ctx) => ctx);
const $isRouteSync = createStore(false)
    .on(routeSyncStarted, () => true)
    .on(routeSyncFinished, () => false);

sample({
    clock: stepPersistRequested,
    source: { last: $lastPersistedStep, ready: $sessionReady, ctx: $routeContext },
    filter: ({ last, ready, ctx }, step) => ready && ctx !== null && step >= 0 && step !== last,
    fn: (_, step) => step,
    target: stepPersistCommitted
});

sample({
    clock: stepPersistCommitted,
    fn: () => ({}),
    target: sessionSaveRequested
});

const delayedRouteNavigation = delay({
    source: routeNavigationRequested,
    timeout: STEP_NAVIGATION_DELAY
});

sample({
    clock: nextStep,
    source: {
        step: $step,
        onboardingId: $onboardingId,
        data: $data,
        answers: $answers
    },
    filter: ({ onboardingId }) => onboardingId !== "email_onboarding",
    fn: ({ step, onboardingId, data, answers }) => {
        const answer = answers.find((a) => a.step === step);
        const answerReply = answer
            ? Array.isArray(answer.data)
                ? answer.data.map((a) => a.value)
                : answer.data.value
            : undefined;

        return {
            onboarding: onboardingId,
            screenCid: data?.id ?? "",
            screenIndex: step,
            reply: answerReply
        };
    },
    target: trackScreenCompletedFx
});

sample({
    clock: setStep,
    source: {
        step: $step,
        onboardingId: $onboardingId,
        data: $data,
        answers: $answers
    },
    filter: ({ onboardingId }) => onboardingId !== "email_onboarding",
    fn: ({ step, onboardingId, data, answers }) => {
        const answer = answers.find((a) => a.step === step);
        const answerReply = answer
            ? Array.isArray(answer.data)
                ? answer.data.map((a) => a.value)
                : answer.data.value
            : undefined;

        return {
            onboarding: onboardingId,
            screenCid: data?.id ?? "",
            screenIndex: step,
            reply: answerReply
        };
    },
    target: trackScreenCompletedFx
});

sample({
    clock: nextStep,
    source: {
        step: $step,
        totalSteps: $totalSteps
    },
    filter: ({ step, totalSteps }) => step >= 0 && step < totalSteps,
    fn: ({ step }) => step + 1,
    target: [$step, stepPersistRequested]
});

// Immediate URL update for user navigation (without delay)
// Use $step.updates to guarantee getting the updated value after nextStep/prevStep
sample({
    clock: $step.updates,
    source: {
        ctx: $routeContext,
        onboardingId: $onboardingId,
        userNavProtection: $userNavProtection,
        totalSteps: $totalSteps
    },
    filter: ({ ctx, userNavProtection, totalSteps }, newStep) => {
        // newStep contains the already updated value (after nextStep/prevStep)
        // Only check for user navigation via userNavProtection
        return !!ctx && userNavProtection && newStep >= 0 && newStep < totalSteps;
    },
    fn: ({ ctx }, newStep) => {
        return buildNavigationPayload(ctx!, newStep);
    },
    target: routeNavigationPlanned
});

// Reset $pendingNav immediately after nextStep/prevStep so the screen displays
sample({
    clock: [nextStep, prevStep],
    fn: () => undefined,
    target: $pendingNav.reinit!
});

sample({
    clock: prevStep,
    source: $step,
    fn: (step) => Math.max(0, step - 1),
    target: [$step, stepPersistRequested]
});

sample({
    clock: setStepStream,
    target: [$step, stepPersistRequested]
});

sample({
    clock: syncStep,
    target: [$step, stepPersistRequested]
});

sample({
    clock: toggleCheckboxId,
    source: $selectedCheckboxes,
    fn: (selectedCheckboxes, id) =>
        selectedCheckboxes.includes(id)
            ? selectedCheckboxes.filter((selectedId) => selectedId !== id)
            : [...selectedCheckboxes, id],
    target: $selectedCheckboxes
});

sample({
    clock: initCheckboxes,
    source: { step: $step, answers: $answers },
    filter: ({ step, answers }) => answers.some((a) => a.step === step),
    fn: ({ step, answers }) => {
        const answer = answers.find((a) => a.step === step);
        return Array.isArray(answer!.data) ? answer!.data.map((a) => a.id) : [];
    },
    target: $selectedCheckboxes
});

sample({
    clock: getAnswer,
    source: $answers,
    filter: (answers, step) => answers.some((a) => a.step === step),
    fn: (answers, step) => {
        const answer = answers.find((a) => a.step === step);
        return !Array.isArray(answer!.data) ? answer!.data : null;
    },
    target: $dependentAnswer
});

sample({
    clock: initOption,
    source: { step: $step, answers: $answers },
    filter: ({ step, answers }) => answers.some((a) => a.step === step),
    fn: ({ step, answers }) => {
        const answer = answers.find((a) => a.step === step);
        return !Array.isArray(answer!.data) ? answer!.data : null;
    },
    target: $selectedOption
});

sample({
    clock: initInputValue,
    source: { step: $step, answers: $answers },
    filter: ({ step, answers }) => answers.some((a) => a.step === step),
    fn: ({ step, answers }) => {
        const answer = answers.find((a) => a.step === step);
        return !Array.isArray(answer!.data) ? answer!.data.value : "";
    },
    target: $inputValue
});

sample({
    clock: initEmailInputValue,
    source: { step: $step, answers: $answers },
    filter: ({ step, answers }) => answers.some((a) => a.step === step),
    fn: ({ step, answers }) => {
        const answer = answers.find((a) => a.step === step);
        return !Array.isArray(answer!.data) ? answer!.data.value : "";
    },
    target: $emailInputValue
});

sample({
    clock: saveAnswer,
    source: {
        step: $step,
        answers: $answers,
        data: $data
    },
    fn: (
        { step, answers, data }: { step: number; answers: IAnswersData[]; data: IScreenData | null },
        answerData: IOption | IOption[]
    ) => {
        let question = "";
        if (data && "title" in data) {
            if (typeof data.title === "string") question = data.title;
            else if (Array.isArray(data.title))
                question = data.title
                    .map((t: string | { text: string }) => (typeof t === "string" ? t : t.text))
                    .join(" ");
        }
        const existingIndex = answers.findIndex((a: IAnswersData) => a.step === step);

        let updatedAnswers: IAnswersData[];
        if (existingIndex !== -1) {
            updatedAnswers = [...answers];
            updatedAnswers[existingIndex] = { step, data: answerData, question };
        } else {
            updatedAnswers = [...answers, { step, data: answerData, question }];
        }
        if (typeof window !== "undefined") {
            sessionStorage.setItem("onboarding_answers", JSON.stringify(updatedAnswers));
        }
        return updatedAnswers;
    },
    target: $answers
});

sample({
    clock: $data,
    source: {
        step: $step,
        onboardingId: $onboardingId,
        data: $data,
        totalSteps: $totalSteps
    },
    filter: ({ data, totalSteps, step }) => data !== null && step < totalSteps,
    fn: ({ step, onboardingId, data }) => ({
        onboarding: onboardingId,
        screenCid: data!.id,
        screenIndex: step
    }),
    target: trackScreenPresentedFx
});

sample({
    clock: $data,
    source: {
        step: $step,
        onboardingId: $onboardingId,
        data: $data
    },
    filter: ({ step, onboardingId }) => step == 1 && onboardingId !== "email_onboarding",
    fn: ({ step, onboardingId, data }) => ({
        onboarding: onboardingId,
        screenCid: data!.id,
        screenIndex: step
    }),
    target: trackSecondScreenPresentedFx
});

sample({
    clock: $data,
    source: {
        step: $step,
        onboardingId: $onboardingId,
        data: $data
    },
    filter: ({ data }) => data?.screenType === ScreenType.PaywallScreen && data.id === "PaywallSceen",
    fn: ({ step, onboardingId, data }) => ({
        onboarding: onboardingId,
        screenCid: data!.id,
        screenIndex: step
    }),
    target: trackOnboardingCompletedFx
});

sample({
    clock: $data,
    source: {
        step: $step,
        onboardingId: $onboardingId,
        data: $data
    },
    filter: ({ data, onboardingId }) => data?.screenType === "PaywallScreen" && onboardingId !== "email_onboarding",
    fn: ({ step, onboardingId, data }) => ({
        onboarding: onboardingId,
        screenCid: data!.id,
        screenIndex: step
    }),
    target: trackProductScreenPresentedFx
});

sample({
    clock: $data,
    source: {
        step: $step,
        onboardingId: $onboardingId,
        data: $data
    },
    filter: ({ data }) =>
        data?.screenType === ScreenType.EmailScreen ||
        data?.screenType === ScreenType.AppleEmailScreen ||
        data?.screenType === ScreenType.FunnelFoxEmailScreen,
    fn: ({ step, onboardingId, data }) => ({
        onboarding: onboardingId,
        screenCid: data!.id,
        screenIndex: step
    }),
    target: trackRegistrationScreenPresentedFx
});

sample({
    source: $data,
    filter: ($data) =>
        $data?.screenType === ScreenType.MultiplyOption || $data?.screenType === ScreenType.FunnelFoxMultiplyOption,
    target: initCheckboxes
});

sample({
    source: $data,
    filter: ($data) =>
        $data?.screenType === ScreenType.SingleOption || $data?.screenType === ScreenType.FunnelFoxSingleOption,
    target: initOption
});

sample({
    source: $data,
    filter: ($data) =>
        $data?.screenType === ScreenType.InputScreen || $data?.screenType === ScreenType.FunnelFoxInputScreen,
    target: initInputValue
});

sample({
    source: $data,
    filter: ($data) =>
        $data?.screenType === ScreenType.EmailScreen ||
        $data?.screenType === ScreenType.AppleEmailScreen ||
        $data?.screenType === ScreenType.FunnelFoxEmailScreen,
    target: initEmailInputValue
});

const contract = new ModelContract(
    {
        $data,
        $step,
        $pendingNav,
        $totalSteps,
        $totalStepsWithoutPaywall,
        $selectedOption,
        $selectedCheckboxes,
        $answers,
        $dependentAnswer,
        $inputValue,
        $emailInputValue,
        $emailFromUrl,
        $onboardingId
    },
    {
        nextStep,
        prevStep,
        requestNextStep,
        requestPrevStep,
        toggleCheckboxId,
        saveAnswer,
        getAnswer,
        initCheckboxes,
        initOption,
        inputValueChanged,
        emailInputValueChanged,
        setStep,
        syncStep,
        emailOnboardingStarted
    },
    { trackOnboardingCompletedFx },
    {}
);

export type IOnboardingCurrentScreenContract = typeof contract;

export class CurrentOnboardingScreenModule extends ModuleDescription<IOnboardingCurrentScreenContract> {
    constructor(
        public onboardingModule: IOnboardingContract,
        public userAuthModule: IUserAuthContract,
        public flagsmithModule: IFlagsmithContract,
        public paymentModule: IPaymentContract,
        public subscriptionModule: ISubscriptionContract,
        public routerModule: IRouterContract,
        public appModule: IAppContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        const $challengeV7EcommpayContext = combine({
            plans: this.paymentModule.stores.$ecommpayPlans,
            currency: this.paymentModule.stores.$currency,
            onboardingId: $onboardingId,
            step: $step
        });

        const $renewEcommpayContext = combine({
            plans: this.paymentModule.stores.$ecommpayPlans,
            subscriptions: this.subscriptionModule.stores.$subscriptions,
            onboardingId: $onboardingId
        });
        const shouldSelectRenewPlan = ({ onboardingId, plans }: { onboardingId: string; plans: IPaymentPlan[] }) =>
            onboardingId === "renew" && plans.some((plan) => plan.tags.includes("renew"));
        const selectRenewEcommpayPlan = ({
            plans,
            subscriptions
        }: {
            plans: IPaymentPlan[];
            subscriptions: ISubscription[];
        }) => {
            const candidates = plans.filter((plan) => plan.tags.includes("renew"));
            const getDefaultPlan = (items: IPaymentPlan[]) => {
                if (items.length === 0) return null;
                const index = items.length > 2 ? 1 : Math.floor(items.length / 2);
                return items[index];
            };
            const currency = getSubscriptionCurrency(subscriptions);
            if (currency) {
                const byCurrency = candidates.filter((plan) => plan.currency === currency);
                const selected = getDefaultPlan(byCurrency);
                if (selected) return selected;
            }
            return getDefaultPlan(candidates) ?? plans[0];
        };

        const $renewSolidgateContext = combine({
            onboardingId: $onboardingId,
            plans: this.paymentModule.stores.$filteredSolidgatePlans
        });

        const onboardingIdUpdates = this.onboardingModule.stores.$onboardingId.updates;

        // ------------------------------------------------------------------
        // The renew paywall's plan sets are configured in the admin (Onboarding
        // plans, onboarding_id = "renew"). Renew renders exactly what this
        // request returns — both providers' sets, prices in every currency and
        // the plan metadata, in the request's locale — and nothing else:
        //   * not the shared $solidgatePlans / $truegatePlans stores, which
        //     other screens fill (the upsell model loads the whole catalog on
        //     lessons and the assistant) — renew used to show that list, with
        //     another screen's language, and then swap it for its own;
        //   * not the full catalog, which put a geolocation round-trip and 170
        //     KB (solidgate) in front of three cards.
        // It starts on entry, alongside the subscriptions, instead of after
        // them: the subscription decides which set to SHOW and in which
        // currency, not what to load. The admin is the only source: a provider
        // with no set there shows no cards.
        const getRenewPlansFx = createEffect(async () => {
            const [solidgate, truegate] = await Promise.all([
                PaymentApi.getOnboardingPlans<ISolidgatePlan[]>("renew", "solidgate").catch(
                    () => [] as ISolidgatePlan[]
                ),
                PaymentApi.getOnboardingPlans<ITruegatePlan[]>("renew", "truegate").catch(
                    () => [] as ITruegatePlan[]
                )
            ]);

            if (!solidgate?.length) console.warn('[renew] no solidgate plans in Onboarding plans for onboarding_id "renew"');
            if (!truegate?.length) console.warn('[renew] no truegate plans in Onboarding plans for onboarding_id "renew"');

            return {
                solidgate: (solidgate ?? []).map((plan) => new SolidgatePlan().restore(plan)),
                truegate: truegate ?? []
            };
        });

        const renewEntered = sample({
            clock: onboardingIdUpdates,
            filter: (onboardingId) => onboardingId === "renew"
        });

        // A language switch while on renew refetches the sets in the new locale
        // (the names come from the plan metadata of that locale).
        const renewLocaleChanged = sample({
            clock: contentLocaleChanged,
            source: $onboardingId,
            filter: (onboardingId) => onboardingId === "renew"
        });

        sample({
            clock: [renewEntered, renewLocaleChanged],
            fn: () => undefined,
            target: getRenewPlansFx
        });

        // The visitor's country feeds $ vs US$, the fallback currency and the
        // truegate payment country. It no longer gates the plans: asked once,
        // in parallel, on entry.
        sample({
            clock: renewEntered,
            fn: () => undefined,
            target: this.paymentModule.events.getUserLocation
        });

        // While a fetch is in flight nothing from a previous one may stay on
        // screen: forget the old sets and hold the skeleton until this one ends.
        sample({
            clock: getRenewPlansFx,
            fn: () => false,
            target: this.paymentModule.events.setRenewPlansSettled
        });

        sample({
            clock: getRenewPlansFx,
            fn: () => [] as SolidgatePlan[],
            target: this.paymentModule.stores.$filteredSolidgatePlans
        });

        sample({
            clock: getRenewPlansFx,
            fn: () => [] as ITruegatePlan[],
            target: this.paymentModule.stores.$filteredTruegatePlans
        });

        sample({
            clock: getRenewPlansFx.doneData,
            source: $onboardingId,
            filter: (onboardingId) => onboardingId === "renew",
            fn: (_, sets) => sets.solidgate,
            target: this.paymentModule.stores.$filteredSolidgatePlans
        });

        sample({
            clock: getRenewPlansFx.doneData,
            source: $onboardingId,
            filter: (onboardingId) => onboardingId === "renew",
            fn: (_, sets) => sets.truegate,
            target: this.paymentModule.stores.$filteredTruegatePlans
        });

        sample({
            clock: getRenewPlansFx.finally,
            fn: () => true,
            target: this.paymentModule.events.setRenewPlansSettled
        });

        const sessionIdUpdates = $sessionId.updates;

        sample({
            clock: this.appModule.events.appMounted,
            filter: () => typeof window !== "undefined",
            fn: () => "solidgate" as const,
            target: this.paymentModule.events.setPaymentSystemFlag
        });

        sample({
            clock: [$data, this.paymentModule.stores.$paymentSystemFlag],
            source: {
                paymentSystem: this.paymentModule.stores.$paymentSystemFlag,
                onboardingId: $onboardingId,
                data: $data
            },
            filter: ({ paymentSystem, onboardingId, data }) => {
                if (typeof window === "undefined") return false;

                const isSolidgateTarget =
                    paymentSystem === "solidgate" &&
                    (onboardingId === "challenge_100k" ||
                        onboardingId === "renew" ||
                        onboardingId === "challenge_100k_v8" ||
                        onboardingId === "challenge_100k_v8_s" ||
                        onboardingId === "challenge_100k_v8_1" ||
                        onboardingId === "challenge_100k_v8_2" ||
                        onboardingId === "challenge_100k_v8_3" ||
                        onboardingId === "challenge_100k_v8_4" ||
                        onboardingId === "challenge_100k_v9" ||
                        onboardingId === "challenge_apple_v2" ||
                        onboardingId === "challenge_100k_v8_es" ||
                        onboardingId === "email_onboarding") &&
                    data?.screenType === "PaywallScreen";

                const isEcommpayTarget =
                    (onboardingId === "challenge_100k_v7_ecommpay" || onboardingId === "challenge_100k_v8_e") &&
                    data?.screenType === "PaywallScreen";

                return isSolidgateTarget || isEcommpayTarget;
            },
            target: [this.paymentModule.events.getUserLocation]
        });

        sample({
            clock: [$data, this.paymentModule.stores.$paymentSystemFlag],
            source: {
                paymentSystem: this.paymentModule.stores.$paymentSystemFlag,
                onboardingId: $onboardingId,
                data: $data
            },
            filter: ({ paymentSystem, onboardingId, data }) => {
                if (typeof window === "undefined") return false;

                return (
                    paymentSystem === "solidgate" &&
                    onboardingId !== "challenge_100k_v2" &&
                    // Renew renders its own /plans/onboarding response (see
                    // getRenewPlansFx) and must not pull the whole catalog.
                    onboardingId !== "renew" &&
                    data?.screenType === "PaywallScreen"
                );
            },
            target: [this.paymentModule.events.getSolidgatePlans /*, this.paymentModule.events.getUserLocation*/]
        });

        sample({
            clock: [$data, this.paymentModule.stores.$paymentSystemFlag],
            source: {
                paymentSystem: this.paymentModule.stores.$paymentSystemFlag,
                onboardingId: $onboardingId,
                data: $data
            },
            filter: ({ paymentSystem, onboardingId, data }) => {
                if (typeof window === "undefined") return false;

                return (
                    (paymentSystem === "ecommpay" ||
                        onboardingId === "challenge_100k_v2" ||
                        onboardingId === "challenge_100k_v7_ecommpay" ||
                        onboardingId === "challenge_100k_v8_e") &&
                    data?.screenType === "PaywallScreen"
                );
            },
            target: this.paymentModule.events.getEcommpayPlans
        });

        sample({
            source: {
                step: $step,
                data: this.onboardingModule.stores.$data
            },
            filter: ({ step, data }) => step >= 0 && step < data.length,
            fn: ({ step, data }) => data[step] ?? null,
            target: $data
        });

        sample({
            clock: $onboardingId,
            source: {
                onboardingId: $onboardingId,
                data: $data
            },
            // Set the renewal paywall screen only once. The clock ($onboardingId) can emit more
            // than once while entering renew; re-running fn would push a NEW $data object each
            // time, and the $data-clocked analytics (onboarding_started, product_screen_presented)
            // would re-fire — i.e. duplicate events. Guarding on the already-set screen keeps a
            // single $data emission.
            filter: ({ onboardingId, data }) =>
                onboardingId === "renew" && data?.id !== "renewal-paywall",
            fn: () =>
                ({
                    id: "renewal-paywall",
                    screenType: ScreenType.PaywallScreen
                }) as IScreenData,
            target: $data
        });

        sample({
            clock: this.onboardingModule.stores.$totalSteps,
            target: $totalSteps
        });

        sample({
            clock: this.onboardingModule.stores.$onboardingId,
            target: $onboardingId
        });

        sample({
            clock: this.onboardingModule.stores.$totalStepsWithoutPaywall,
            target: $totalStepsWithoutPaywall
        });

        sample({
            clock: $data,
            source: {
                onboardingId: $onboardingId,
                step: $step
            },
            filter: ({ step, onboardingId }) =>
                step === 0 &&
                onboardingId !== "" &&
                onboardingId !== "email_onboarding" &&
                typeof window !== "undefined" &&
                !window.location.pathname.includes("/success-payment") &&
                !window.location.pathname.includes("/renew-success"),
            fn: ({ onboardingId }) => ({ onboarding: onboardingId }),
            target: this.onboardingModule.effects.trackOnboardingStartedFx
        });

        sample({
            clock: this.paymentModule.events.getSpecialOffer,
            source: {
                step: $step,
                onboardingId: $onboardingId,
                totalSteps: $totalSteps,
                data: $data
            },
            filter: ({ onboardingId }) => onboardingId !== "email_onboarding",
            fn: ({ step, onboardingId, data }) => ({
                onboarding: onboardingId,
                screenCid: data!.id,
                screenIndex: step
            }),
            target: trackProductScreenPresentedFx
        });

        sample({
            clock: [this.paymentModule.stores.$ecommpayPlans, $onboardingId, $step],
            source: {
                $data,
                $onboardingId,
                $step
            },
            filter: ({ $data, $onboardingId }) => $data?.screenType === "PaywallScreen" || $onboardingId === "renew",
            fn: ({ $onboardingId, $step }) => {
                if ($onboardingId === "challenge_v1_our") return ["usd"];
                if ($onboardingId === "challenge_v2_our") return ["rupee"];
                if ($onboardingId === "challenge_100k") return ["challenge_100k", "usd"];
                if ($onboardingId === "challenge_100k_v2") return ["challenge_100k", "usd"];
                if ($onboardingId === "challenge_100k_v3") return ["challenge_100k_v3"];
                if ($onboardingId === "challenge_100k_v4") return ["challenge_100k_v4"];
                if ($onboardingId === "challenge_100k_v5") return ["challenge_100k_v5"];
                if ($onboardingId === "challenge_100k_v6") return ["challenge_100k_v6"];
                if ($onboardingId === "challenge_100k_v7") return ["challenge_100k_v7"];
                if ($onboardingId === "challenge_100k_v8") return ["challenge_100k_v8"];
                if ($onboardingId === "challenge_100k_v8_s") return ["challenge_100k_v8"];
                if ($onboardingId === "challenge_100k_v8_1") return ["challenge_100k_v8"];
                if ($onboardingId === "challenge_100k_v8_3") return ["challenge_100k_v8_3"];
                if ($onboardingId === "challenge_100k_v8_4") return ["challenge_100k_v8_4"];
                if ($onboardingId === "challenge_100k_v9") return ["challenge_100k_v9"];
                if ($onboardingId === "challenge_apple_v2") return ["challenge_apple_v2", "challenge_100k_v9"];
                if ($onboardingId === "email_onboarding") return ["challenge_100k_v8"];
                if ($onboardingId === "challenge_100k_v7_ecommpay") {
                    if ($step === CHALLENGE_100K_V7_DISCOUNT_PAYWALL_STEP)
                        return ["challenge_100k_v7_ecommpay_discount"];
                    return ["challenge_100k_v7_ecommpay"];
                }
                if ($onboardingId === "challenge_100k_v8_e") {
                    if ($step === CHALLENGE_100K_V8_DISCOUNT_PAYWALL_STEP) return ["challenge_100k_v8_e_discount"];
                    return ["challenge_100k_v8_e"];
                }
                if ($onboardingId === "renew") return ["renew"];
                return [];
            },
            target: this.paymentModule.events.setPaymentPlanTags
        });

        sample({
            source: $challengeV7EcommpayContext,
            filter: ({ onboardingId, plans }) =>
                (onboardingId === "challenge_100k_v7_ecommpay" || onboardingId === "challenge_100k_v8_e") &&
                plans.length > 0,
            fn: ({ plans, currency, step, onboardingId }) => {
                const isV7 = onboardingId === "challenge_100k_v7_ecommpay";
                const preferredTag = isV7
                    ? step === CHALLENGE_100K_V7_DISCOUNT_PAYWALL_STEP
                        ? CHALLENGE_100K_V7_ECOMMPAY_DISCOUNT_TAG
                        : CHALLENGE_100K_V7_ECOMMPAY_TAG
                    : step === CHALLENGE_100K_V8_DISCOUNT_PAYWALL_STEP
                        ? CHALLENGE_100K_V8_E_DISCOUNT_TAG
                        : CHALLENGE_100K_V8_E_TAG;

                const fallbackTags = isV7
                    ? [CHALLENGE_100K_V7_ECOMMPAY_TAG, CHALLENGE_100K_V7_ECOMMPAY_DISCOUNT_TAG]
                    : [CHALLENGE_100K_V8_E_TAG, CHALLENGE_100K_V8_E_DISCOUNT_TAG];
                const getCandidates = (tag: string) => plans.filter((plan) => plan.tags.includes(tag));
                const getMiddlePlan = (items: IPaymentPlan[]) => {
                    if (items.length === 0) return null;
                    const sorted = [...items].sort((a, b) => a.newPrice - b.newPrice);
                    const index = Math.min(Math.floor(sorted.length / 2), sorted.length - 1);
                    return sorted[index];
                };

                let candidates = getCandidates(preferredTag);
                if (candidates.length === 0) {
                    candidates = plans.filter((plan) => fallbackTags.some((tag) => plan.tags.includes(tag)));
                }

                if (currency) {
                    const byCurrency = candidates.filter((plan) => plan.currency === currency);
                    const middleByCurrency = getMiddlePlan(byCurrency);
                    if (middleByCurrency) return middleByCurrency;
                }

                return getMiddlePlan(candidates) ?? plans[0];
            },
            target: this.paymentModule.events.setPlan
        });

        sample({
            source: $renewEcommpayContext,
            filter: shouldSelectRenewPlan,
            fn: selectRenewEcommpayPlan,
            target: this.paymentModule.events.setPlan
        });
        sample({
            source: $renewEcommpayContext,
            clock: this.appModule.events.routePathNameChanged,
            filter: (context, path) =>
                shouldSelectRenewPlan(context) && path.includes("/profile/settings/subscriptions/renew"),
            fn: selectRenewEcommpayPlan,
            target: this.paymentModule.events.setPlan
        });

        sample({
            source: $renewSolidgateContext,
            filter: ({ onboardingId, plans }) => onboardingId === "renew" && plans.length > 0,
            fn: ({ plans }) => {
                const index = plans.length > 2 ? 1 : Math.floor(plans.length / 2);
                return plans[index] ?? plans[0];
            },
            target: this.paymentModule.events.setSolidgatePlan
        });

        sample({
            clock: [
                this.paymentModule.stores.$solidgatePlans,
                this.paymentModule.stores.$specialOfferStep,
                onboardingIdUpdates
            ],
            source: {
                step: $step,
                totalSteps: $totalSteps,
                onboardingId: $onboardingId,
                plans: this.paymentModule.stores.$solidgatePlans,
                specialOfferStep: this.paymentModule.stores.$specialOfferStep,
                data: $data,
                ctx: $routeContext
            },
            // Renew fills $filteredSolidgatePlans from its own response.
            filter: ({ data, onboardingId }) => data?.screenType === "PaywallScreen" && onboardingId !== "renew",
            fn: ({ onboardingId, plans, specialOfferStep, step, ctx }) => {
                const appleChallenge100kIds = [
                    "9308fc9e-26fe-4b6b-8598-0309d208845a",
                    "9308fc9e-26fe-4b6b-8598-0309d208845a",
                    "9308fc9e-26fe-4b6b-8598-0309d208845a"
                ];

                const challenge100kIds = [
                    "9308fc9e-26fe-4b6b-8598-0309d208845a",
                    "6ae7a950-19b5-407f-aa30-63911e85fa45",
                    "38b72507-3951-4c49-a6d8-397eaf831f71"
                ];

                const challenge100kWithDiscountIds = [
                    "fa98e923-3688-413c-840c-1fc82ff3e569",
                    "51dd6cc6-6c3f-4dd8-b4e1-42376b8c6a66",
                    "1ae4739f-ef2f-41ea-9277-d187b618d338"
                ];

                const paywallV2 = [
                    "f7d6ef59-b4fa-4d06-ac5d-833728afea3c",
                    "237ea633-6ad2-4084-a847-34475116c43c",
                    "6b400d7f-5562-4cba-bcbe-c6128d3654dd"
                ];

                const paywallV2SpecialOfferIds = [
                    "78100e28-72d0-4fa0-a2fc-3fe2d81efc8d",
                    "6f8f7149-ce27-4a64-9fc1-d92db94b9b25",
                    "43c4a206-4f54-491e-88f1-2adeb5fa5aeb"
                ];

                let targetIds: string[] = [];

                if (onboardingId === "challenge_v1_our") targetIds = challenge100kIds;
                if (onboardingId === "challenge_v2_our")
                    targetIds = specialOfferStep === 2 ? paywallV2SpecialOfferIds : paywallV2;

                if (onboardingId === "apple_challenge_100k") targetIds = appleChallenge100kIds;
                if (onboardingId === "challenge_100k" && step === CHALLENGE_100K_PAYWALL_STEP)
                    targetIds = challenge100kIds;

                if (onboardingId === "challenge_100k" && step === CHALLENGE_100K_DISCOUNT_PAYWALL_STEP)
                    targetIds = challenge100kWithDiscountIds;

                if (onboardingId === "challenge_100k_v3" && step === CHALLENGE_100K_V3_PAYWALL_STEP)
                    targetIds = challenge100kV3Ids;

                if (onboardingId === "challenge_100k_v3" && step === CHALLENGE_100K_V3_DISCOUNT_PAYWALL_STEP)
                    targetIds = challenge100kV3WithDiscountIds;

                if (onboardingId === "challenge_100k_v4" && step === CHALLENGE_100K_V4_PAYWALL_STEP)
                    targetIds = challenge100kV4Ids;

                if (onboardingId === "challenge_100k_v4" && step === CHALLENGE_100K_V4_DISCOUNT_PAYWALL_STEP)
                    targetIds = challenge100kV4WithDiscountIds;

                if (onboardingId === "challenge_100k_v5" && step === CHALLENGE_100K_V5_PAYWALL_STEP)
                    targetIds = challenge100kV5Ids;

                if (onboardingId === "challenge_100k_v5" && step === CHALLENGE_100K_V5_DISCOUNT_PAYWALL_STEP)
                    targetIds = challenge100kV5WithDiscountIds;

                if (onboardingId === "challenge_100k_v6" && step === CHALLENGE_100K_V6_PAYWALL_STEP)
                    targetIds = challenge100kV6Ids;

                if (onboardingId === "challenge_100k_v6" && step === CHALLENGE_100K_V6_DISCOUNT_PAYWALL_STEP)
                    targetIds = challenge100kV6WithDiscountIds;

                if (onboardingId === "challenge_100k_v7" && step === CHALLENGE_100K_V7_PAYWALL_STEP)
                    targetIds = challenge100kV7Ids;

                if (onboardingId === "challenge_100k_v7" && step === CHALLENGE_100K_V7_DISCOUNT_PAYWALL_STEP)
                    targetIds = challenge100kV7WithDiscountIds;

                if (
                    (onboardingId === "challenge_100k_v8" ||
                        onboardingId === "challenge_100k_v8_s" ||
                        onboardingId === "challenge_100k_v8_es" ||
                        onboardingId === "challenge_100k_v8_2") &&
                    step === CHALLENGE_100K_V8_PAYWALL_STEP
                ) {
                    targetIds = challenge100kV8Ids;
                }

                if (
                    (onboardingId === "challenge_100k_v8" ||
                        onboardingId === "challenge_100k_v8_s" ||
                        onboardingId === "challenge_100k_v8_es" ||
                        onboardingId === "challenge_100k_v8_2") &&
                    step === CHALLENGE_100K_V8_DISCOUNT_PAYWALL_STEP
                ) {
                    targetIds = challenge100kV8WithDiscountIds;
                }

                if (onboardingId === "challenge_100k_v8_3" && step === CHALLENGE_100K_V8_3_PAYWALL_STEP) {
                    targetIds = challenge100kV8_3Ids;
                }

                if (onboardingId === "challenge_100k_v8_3" && step === CHALLENGE_100K_V8_3_DISCOUNT_PAYWALL_STEP) {
                    targetIds = challenge100kV8_3WithDiscountIds;
                }

                if (onboardingId === "challenge_100k_v8_4" && step === CHALLENGE_100K_V8_4_PAYWALL_STEP) {
                    targetIds = challenge100kV8_4Ids;
                }

                if (onboardingId === "challenge_100k_v8_4" && step === CHALLENGE_100K_V8_4_DISCOUNT_PAYWALL_STEP) {
                    targetIds = challenge100kV8_4WithDiscountIds;
                }

                if (onboardingId === "challenge_100k_v9" && step === CHALLENGE_100K_V9_PAYWALL_STEP) {
                    targetIds = challenge100kV9Ids;
                }

                if (onboardingId === "challenge_100k_v9" && step === CHALLENGE_100K_V9_DISCOUNT_PAYWALL_STEP) {
                    targetIds = challenge100kV9WithDiscountIds;
                }

                if (onboardingId === "challenge_apple_v2" && step === CHALLENGE_APPLE_V2_PAYWALL_STEP) {
                    targetIds = challengeAppleV2Ids;
                }

                if (onboardingId === "challenge_apple_v2" && step === CHALLENGE_APPLE_V2_DISCOUNT_PAYWALL_STEP) {
                    targetIds = challengeAppleV2WithDiscountIds;
                }

                if (onboardingId === "challenge_100k_v8_1" && step === CHALLENGE_100K_V8_PAYWALL_STEP)
                    targetIds = challenge100kV8Ids;

                if (onboardingId === "challenge_100k_v8_1" && step === CHALLENGE_100K_V8_DISCOUNT_PAYWALL_STEP)
                    targetIds = challenge100kV8WithDiscountIds;

                if (onboardingId === "email_onboarding") {
                    const urlParams = ctx ? new URLSearchParams(ctx.search || "") : null;
                    const hasDiscount = urlParams?.get("discount") === "true";

                    if (hasDiscount || step === 1) {
                        targetIds = challenge100kV8WithDiscountIds;
                    } else {
                        targetIds = challenge100kV8Ids;
                    }
                }

                return targetIds
                    ?.map((id) => plans?.find((plan) => plan.id === id))
                    ?.filter(Boolean) as SolidgatePlan[];
            },

            target: this.paymentModule.stores.$filteredSolidgatePlans
        });

        sample({
            clock: this.appModule.events.routeStateChanged,
            fn: ({ path, params, search }) => parseRouteContext({ path, params, search }),
            target: routeContextUpdated
        });

        const getOnboardingAnswersByEmailFx = createEffect(async (email: string) => {
            try {
                const response = await StudentApi.getOnboardingAnswersByEmail(email);
                return response.answers;
            } catch (error: any) {
                if (error?.status === 404) {
                    return [];
                }
                throw error;
            }
        });

        const setUserIdByEmailFx = createEffect(async (email: string) => {
            try {
                const response = await StudentApi.getUserIdByEmail(email);
                if (response?.userId) {
                    AnalyticsService.setUserId(response.userId);
                }
            } catch (error) {
                console.error("Failed to get userID by email:", error);
            }
        });

        const prepareSignUpData = (email: string) => ({
            email: email.toLowerCase(),
            deviceId: localStorage.getItem("user_uuid") || "",
            qs: sessionStorage.getItem("query_params") || "",
            gaClientId: "",
            fbPixelId: "",
            fbClickId: ""
        });

        sample({
            clock: emailOnboardingStarted,
            filter: () => {
                return typeof window !== "undefined" && window.location.search?.includes("email=");
            },
            fn: () => {
                if (typeof window === "undefined") return null;
                const urlParams = new URLSearchParams(window.location.search);
                const email = urlParams.get("email");

                if (email && !sessionStorage.getItem("email")) {
                    sessionStorage.setItem("email", email);
                }

                return email;
            },
            target: $emailFromUrl
        });

        // Send onboarding_started event for email_onboarding
        sample({
            clock: emailOnboardingStarted,
            source: {
                onboardingId: $onboardingId,
                step: $step
            },
            filter: ({ onboardingId, step }) => onboardingId === "email_onboarding" && step !== 1,
            fn: () => ({ onboarding: "email_onboarding" }),
            target: this.onboardingModule.effects.trackOnboardingStartedFx
        });

        // Send products_screen_presented event for email_onboarding
        sample({
            clock: emailOnboardingStarted,
            source: {
                step: $step,
                onboardingId: $onboardingId,
                data: $data,
                ctx: $routeContext
            },
            filter: ({ onboardingId, data }) => onboardingId === "email_onboarding" && data !== null,
            fn: ({ step, onboardingId, data, ctx }) => {
                const urlParams = ctx ? new URLSearchParams(ctx.search || "") : null;
                const hasDiscount = urlParams?.get("discount") === "true";
                const isDiscountScreen = hasDiscount || step === 1;

                return {
                    onboarding: onboardingId,
                    screenCid: isDiscountScreen ? "PaywallScreenDiscount" : data!.id,
                    screenIndex: step
                };
            },
            target: trackProductScreenPresentedFx
        });

        sample({
            clock: $emailFromUrl.updates,
            source: {
                onboardingId: $onboardingId,
                emailFromUrl: $emailFromUrl
            },
            filter: ({ onboardingId, emailFromUrl }) => {
                return onboardingId === "email_onboarding" && emailFromUrl !== null && emailFromUrl !== "";
            },
            fn: ({ emailFromUrl }) => emailFromUrl!,
            target: setUserIdByEmailFx
        });

        sample({
            clock: setUserIdByEmailFx.done,
            source: {
                onboardingId: $onboardingId,
                emailFromUrl: $emailFromUrl
            },
            filter: ({ onboardingId, emailFromUrl }) => {
                return onboardingId === "email_onboarding" && emailFromUrl !== null && emailFromUrl !== "";
            },
            fn: ({ emailFromUrl }) => emailFromUrl!.toLowerCase(),
            target: this.subscriptionModule.effects.checkAnySubscriptionByEmailFx
        });

        sample({
            clock: $emailFromUrl.updates,
            source: {
                onboardingId: $onboardingId
            },
            filter: ({ onboardingId }, email) => {
                return email !== null && onboardingId !== "email_onboarding";
            },
            fn: (_, email) => email!.toLowerCase(),
            target: this.subscriptionModule.effects.checkAnySubscriptionByEmailFx
        });

        // Initialize subscription check for email_onboarding on mount/reload
        // This handles the case when page is reloaded and emailOnboardingStarted wasn't called
        sample({
            clock: [$onboardingId.updates, setOnboardingDataById],
            source: {
                onboardingId: $onboardingId,
                subscriptionCheckCompleted: this.subscriptionModule.stores.$subscriptionCheckCompleted,
                emailFromUrl: $emailFromUrl
            },
            filter: ({ onboardingId, subscriptionCheckCompleted, emailFromUrl }) => {
                if (onboardingId !== "email_onboarding") return false;
                if (subscriptionCheckCompleted) return false;
                if (emailFromUrl !== null) return false; // Already set, will trigger check via $emailFromUrl.updates

                // Check if email is available in URL or sessionStorage
                if (typeof window === "undefined") return false;
                const urlParams = new URLSearchParams(window.location.search);
                const emailInUrl = urlParams.get("email");
                const emailInStorage = sessionStorage.getItem("email");
                return !!(emailInUrl || emailInStorage);
            },
            fn: () => {
                if (typeof window === "undefined") return null;
                const urlParams = new URLSearchParams(window.location.search);
                const emailInUrl = urlParams.get("email");
                const emailInStorage = sessionStorage.getItem("email");
                const email = emailInUrl || emailInStorage;

                if (email && !sessionStorage.getItem("email")) {
                    sessionStorage.setItem("email", email);
                }

                return email;
            },
            target: $emailFromUrl
        });

        // Save flag if signup fails with "Email already exists" in email_onboarding
        // This means user had account (possibly with inactive subscription)
        // After purchase, redirect to login page instead of upsell
        sample({
            clock: this.userAuthModule.effects.signUpRequestedFx.fail,
            source: { onboardingId: $onboardingId },
            filter: ({ onboardingId }) => {
                return onboardingId === "email_onboarding";
            },
            fn: () => {
                // Save flag that user had account (possibly with inactive subscription)
                // After purchase, redirect to login page instead of upsell
                if (typeof window !== "undefined") {
                    sessionStorage.setItem("had_inactive_subscription", "true");
                }
            }
        });

        // Load onboarding answers and register user if user has no subscription
        sample({
            clock: this.subscriptionModule.effects.checkAnySubscriptionByEmailFx.doneData,
            source: { onboardingId: $onboardingId, emailFromUrl: $emailFromUrl },
            filter: ({ onboardingId, emailFromUrl }, hasSubscription) => {
                return onboardingId === "email_onboarding" && !hasSubscription && emailFromUrl !== null;
            },
            fn: ({ emailFromUrl }) => {
                return emailFromUrl!;
            },
            target: [
                getOnboardingAnswersByEmailFx,
                this.userAuthModule.events.signUpRequested.prepend(prepareSignUpData)
            ]
        });

        // Also load answers and register user if subscription check failed (treat as no subscription)
        sample({
            clock: this.subscriptionModule.effects.checkAnySubscriptionByEmailFx.fail,
            source: { onboardingId: $onboardingId, emailFromUrl: $emailFromUrl },
            filter: ({ onboardingId, emailFromUrl }) => {
                return onboardingId === "email_onboarding" && emailFromUrl !== null;
            },
            fn: ({ emailFromUrl }) => {
                return emailFromUrl!;
            },
            target: [
                getOnboardingAnswersByEmailFx,
                this.userAuthModule.events.signUpRequested.prepend(prepareSignUpData)
            ]
        });

        sample({
            clock: getOnboardingAnswersByEmailFx.doneData,
            fn: (answers) => {
                if (typeof window !== "undefined") {
                    sessionStorage.setItem("onboarding_answers", JSON.stringify(answers));
                }
                return answers;
            },
            target: $answers
        });

        sample({
            clock: routeContextUpdated,
            source: {
                ctx: $routeContext,
                step: $step,
                pendingNav: $pendingNav,
                userNavProtection: $userNavProtection,
                isRouteSync: $isRouteSync
            },
            filter: ({ ctx, step, pendingNav, userNavProtection, isRouteSync }) => {
                // Don't sync if:
                // 1. User navigation in progress (pendingNav !== "none" or userNavProtection === true)
                // 2. Synchronization is already in progress
                return ctx !== null && ctx.step !== step && pendingNav === "none" && !userNavProtection && !isRouteSync;
            },
            fn: ({ ctx }) => ctx!.step,
            target: [routeSyncStarted, syncStep]
        });

        sample({
            clock: $step,
            source: { isRouteSync: $isRouteSync },
            filter: ({ isRouteSync }) => isRouteSync,
            fn: () => undefined,
            target: routeSyncFinished
        });

        sample({
            clock: $step,
            source: {
                ctx: $routeContext,
                isRouteSync: $isRouteSync,
                pendingNav: $pendingNav,
                userNavProtection: $userNavProtection
            },
            filter: ({ ctx, isRouteSync, pendingNav, userNavProtection }, step) => {
                // Don't update URL if:
                // 1. User navigation in progress (pendingNav !== "none" or userNavProtection === true)
                // 2. Synchronization is already in progress
                return !!ctx && !isRouteSync && pendingNav === "none" && !userNavProtection && ctx.step !== step;
            },
            fn: ({ ctx }, step) => ({ ctx: ctx as RouteContext, step }),
            target: routeNavigationRequested
        });

        sample({
            clock: delayedRouteNavigation,
            source: $step,
            filter: (currentStep, payload) => currentStep === payload.step,
            fn: (_, { ctx, step }) => buildNavigationPayload(ctx, step),
            target: routeNavigationPlanned
        });

        sample({
            clock: routeNavigationPlanned,
            filter: (s) => s.replace,
            fn: (s) => s.url,
            target: this.routerModule.events.routerReplace
        });

        sample({
            clock: routeNavigationPlanned,
            filter: (s) => !s.replace,
            fn: (s) => s.url,
            target: this.routerModule.events.routerPush
        });

        // Reset protection only when URL is actually synced with current step
        // This ensures synchronization won't happen until URL is updated
        sample({
            clock: routeContextUpdated,
            source: { ctx: $routeContext, step: $step, userNavProtection: $userNavProtection },
            filter: ({ ctx, step, userNavProtection }) => {
                // Reset protection only if URL already matches current step
                // and protection was active (meaning this is URL update from user navigation)
                return userNavProtection && ctx !== null && ctx.step === step;
            },
            fn: () => undefined,
            target: $userNavProtection.reinit!
        });

        sample({
            clock: [routeContextUpdated, onboardingIdUpdates],
            source: {
                ctx: $routeContext,
                onboardingId: $onboardingId,
                sessionId: $sessionId,
                ready: $sessionReady
            },
            filter: ({ ctx, onboardingId, sessionId, ready }) => {
                if (!ctx || !onboardingId) return false;
                const routeSessionId = getSessionIdFromSearch(ctx.search);
                if (!ready) return true;
                return Boolean(routeSessionId && routeSessionId !== sessionId);
            },
            fn: ({ ctx, onboardingId }) => ({ onboardingId: onboardingId!, search: ctx!.search }),
            target: sessionEnsureRequested
        });

        sample({
            clock: [routeContextUpdated, sessionIdUpdates],
            source: { ctx: $routeContext, sessionId: $sessionId, ready: $sessionReady },
            filter: ({ ctx, sessionId, ready }) => !!ctx && ready && !!sessionId && !getSessionIdFromSearch(ctx.search),
            fn: ({ sessionId }) => sessionId!,
            target: sessionUrlUpdateRequested
        });

        sample({
            clock: ensureSessionFx.doneData,
            fn: ({ session }) => session.sessionId,
            target: sessionUrlUpdateRequested
        });

        sample({
            clock: ensureSessionFx.doneData,
            filter: ({ session }) => (session.answers?.length ?? 0) > 0 || (session.lastScreenIdx ?? 0) > 0,
            fn: ({ session }) => ({
                answers: session.answers ?? [],
                step: session.lastScreenIdx ?? 0
            }),
            target: sessionHydrateRequested
        });

        sample({
            clock: sessionHydrateRequested,
            fn: ({ step }) => Math.max(step, 0),
            target: setHydratedStep
        });

        sample({
            clock: sessionHydrateRequested,
            fn: ({ answers }) => answers,
            target: persistAnswersFx
        });

        sample({
            clock: sessionInvalidated,
            fn: () => [],
            target: persistAnswersFx
        });

        sample({
            clock: sessionInvalidated,
            fn: () => null,
            target: sessionUrlUpdateRequested
        });

        sample({
            clock: sessionInvalidated,
            source: { ctx: $routeContext, onboardingId: $onboardingId },
            filter: ({ ctx, onboardingId }) => ctx !== null && Boolean(onboardingId),
            fn: ({ ctx, onboardingId }) => ({ onboardingId: onboardingId!, search: ctx!.search }),
            target: sessionEnsureRequested
        });

        sample({
            clock: sessionSaveDebounced,
            source: { sessionId: $sessionId, answers: $answers, step: $step },
            filter: ({ sessionId }) => Boolean(sessionId),
            fn: ({ sessionId, answers, step }) => ({
                sessionId: sessionId as string,
                answers,
                lastScreenIdx: step >= 0 ? step : 0
            }),
            target: updateSessionFx
        });

        sample({
            clock: saveAnswer,
            source: { data: $data, sessionState: $sessionState },
            filter: ({ data, sessionState }) =>
                (data?.screenType === ScreenType.EmailScreen ||
                    data?.screenType === ScreenType.AppleEmailScreen ||
                    data?.screenType === ScreenType.FunnelFoxEmailScreen) &&
                sessionState !== "email_captured" &&
                sessionState !== "checkout" &&
                sessionState !== "done",
            fn: () => ({ state: "email_captured" as OnboardingSessionState }),
            target: sessionSaveRequested
        });

        sample({
            clock: $data,
            source: { sessionState: $sessionState },
            filter: ({ sessionState }, data) =>
                data?.screenType === ScreenType.PaywallScreen && sessionState !== "checkout" && sessionState !== "done",
            fn: () => ({ state: "checkout" as OnboardingSessionState }),
            target: sessionSaveRequested
        });

        sample({
            clock: this.onboardingModule.events.clearOnboardingSession,
            fn: () => ({ state: "done" as OnboardingSessionState }),
            target: sessionSaveRequested
        });

        sample({
            clock: sessionSaveRequested,
            filter: (payload): payload is { state: OnboardingSessionState } => Boolean(payload?.state),
            fn: (payload) => payload.state!,
            target: sessionStateUpdateRequested
        });

        sample({
            clock: sessionSaveRequested,
            filter: (payload) => !payload?.state,
            target: sessionPassiveSaveRequested
        });

        sample({
            clock: sessionStateUpdateRequested,
            source: { sessionId: $sessionId, answers: $answers, step: $step },
            filter: ({ sessionId }) => Boolean(sessionId),
            fn: ({ sessionId, answers, step }, state) => ({
                sessionId: sessionId as string,
                answers,
                lastScreenIdx: step >= 0 ? step : 0,
                state
            }),
            target: updateSessionFx
        });

        sample({
            clock: updateSessionFx.doneData,
            filter: (session) => session.state === "done",
            target: sessionInvalidated
        });

        sample({
            clock: updateSessionFx.failData,
            filter: (error) => shouldResetSession(error),
            target: sessionInvalidated
        });

        sample({
            clock: sessionUrlUpdateRequested,
            source: { path: this.appModule.stores.$activeRoute, ctx: $routeContext },
            filter: ({ ctx }) => ctx !== null,
            fn: ({ path, ctx }, sessionId) => buildUrlWithSessionId(path, ctx!.search, sessionId),
            target: this.routerModule.events.routerReplace
        });

        reset({
            clock: this.onboardingModule.events.clearOnboardingSession,
            target: [
                $step,
                $data,
                $onboardingId,
                $totalSteps,
                $selectedCheckboxes,
                $selectedOption,
                $answers,
                $dependentAnswer,
                $inputValue,
                $emailInputValue,
                $emailFromUrl
            ]
        });
    }
}
